import { useCallback, useEffect, useRef, useState } from "react";
import { drw } from "./api";

/** Filter memory (DRW §8): per user / project / view, server-side. `load()` resolves the
 *  remembered state once; `save(state)` debounces 600ms. While a plan link is applied on top
 *  (`suspended`), nothing is written, so leaving the link restores what was remembered. */
export function useViewState(projectId, view) {
  const [remembered, setRemembered] = useState(undefined);   // undefined = not loaded yet
  const timer = useRef(null);
  const suspended = useRef(false);

  useEffect(() => {
    let alive = true;
    setRemembered(undefined);
    if (!projectId || !view) { setRemembered(null); return undefined; }
    drw.get("/view-state", { params: { project_id: projectId, view } })
      .then(({ data }) => { if (alive) setRemembered(data.state && Object.keys(data.state).length ? data.state : null); })
      .catch(() => { if (alive) setRemembered(null); });
    return () => { alive = false; };
  }, [projectId, view]);

  const save = useCallback((state) => {
    if (!projectId || !view || suspended.current) return;
    clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      drw.put("/view-state", { project_id: projectId, view, state }).catch(() => {});
    }, 600);
  }, [projectId, view]);

  /** DRW-LINKS — write NOW and wait for it. Used when the user is about to leave the view (a
   *  drawing link): a debounced save would be thrown away by the navigation. `as` writes the
   *  snapshot under its own one-shot key so Back restores what the user actually left, even if the
   *  remembered filters move on somewhere else. */
  const saveNow = useCallback(async (state, as = "") => {
    if (!projectId) return;
    clearTimeout(timer.current);
    await drw.put("/view-state", { project_id: projectId, view: as || view, state });
  }, [projectId, view]);

  const reset = useCallback(async () => {
    clearTimeout(timer.current);
    try { await drw.delete("/view-state", { params: { project_id: projectId, view } }); } catch { /* offline */ }
    setRemembered(null);
  }, [projectId, view]);

  const suspend = useCallback((on) => { suspended.current = on; if (on) clearTimeout(timer.current); }, []);

  return { remembered, loaded: remembered !== undefined, save, saveNow, reset, suspend };
}
